import { Component, OnInit, Input, HostListener, AfterViewChecked } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DateService } from 'src/app/shared/services/date.service';
import { PurlSearchParams } from '../interfaces/purl-search-params.interface';
import { PtlWindowLocationService } from '@ptl/global-components';
import { ProducerService } from '../producer.service';
import { PurlCount } from '../interfaces/purl-count-response.interface';
import { REFERRALTYPE, REFERRAL_TYPE_DISPLAY } from '../interfaces/referral-type.enum';
import { LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PurlSearchResponse } from '../interfaces/purl-search-response.interface';
import { environment } from 'src/environments/environment';
import { BooleanInput } from '@ptl/global-components';

export interface StatusBucket {
  emailSent?: number;
  clicked?: number;
  submittedApps?: number;
}

@Component({
    selector: 'app-purl-dashboard',
    templateUrl: './purl-dashboard.component.html',
    styleUrls: ['./purl-dashboard.component.scss'],
    standalone: false
})
export class PurlDashboardComponent implements OnInit, AfterViewChecked {
  title = 'Sum of PURL Steps Taken by Customers';
  dynamicResize = true;
  type = 'BarChart';
  data = [];
  columnNames = [];
  options = {
    hAxis: {
      titleTextStyle: {
        fontSize: 18,
        bold: true
      },
      minValue: 0,
      format: '0'
    },
    vAxis: {
      bold: true,
      titleTextStyle: {
        fontSize: 18,
        bold: true
      },
      minValue: 0
    },
    isStacked: true,
  };
  width = 1000;
  height = 400;

  maxDate = new Date();

  displayedColumns: string[] = ['email', 'emailSent', 'referralId', 'referralType', 'linkClicked', 'appInProgress', 'appSubmitted'];

  searchForm: UntypedFormGroup;

  @Input() referralPageUrl: string;
  @Input() @BooleanInput() showAppInProgressStep: boolean;
  @Input() @BooleanInput() showAppReferralType: string;
  @Input() showStatusFilter: string | boolean;
  @Input() subheading: string;
  @Input() @BooleanInput() isDirectReferralView: boolean;
  @Input() barChartTitle: string;
  @Input() xAxisTitle: string;
  @Input() yAxisTitle: string;
  @Input() @BooleanInput() isPassiveReferralView: boolean; 
  @Input() @BooleanInput() showLegend: boolean;

  noOfPlans = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  referralsPerPage = 10;
  currentPage = 1;
  totalApplications = 0;
  totalPages = 0;
  firstAppNo = 1;
  lastAppNo = this.referralsPerPage;
  tableView = false;
  searchParams: PurlSearchParams;
  selectedStep = '';
  showReferralStatusFilter = false;
  hidePaginator = false;
  agencyId: string;
  referralTypes: string[];
  isMN: boolean;

  referralStatusSteps = [];

  constructor(
    private fb: UntypedFormBuilder,
    private dateService: DateService,
    private windowLocationService: PtlWindowLocationService,
    private reducerUtilityService: ReducerUtilityService,
    private producerService: ProducerService
  ) {
    this.searchForm = this.fb.group({
      emailId: this.fb.control('', [Validators.maxLength(75)]),
      fromDate: this.fb.control('', []),
      toDate: this.fb.control('', [])
    });
  }

  ngAfterViewChecked(): void {
    // removing hidden table section from the chart.
    let tbl = document.querySelector('[aria-label="A tabular representation of the data in the chart."]');
    if (tbl) {
      tbl.remove();
    }
  }

  referrals: any;

  async ngOnInit() {
    this.options.hAxis['title'] = this.xAxisTitle ? this.xAxisTitle : 'Number of Actions';
    this.options.vAxis['title'] = this.yAxisTitle ? this.yAxisTitle : 'PURL Step';
    if(this.barChartTitle){
      this.title = this.barChartTitle
    }
  
    this.options['legend'] = this.showLegend ? { position: 'bottom', alignment: 'center', orientation: 'vertical'} : { position: 'none' };
  
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const lob = this.reducerUtilityService.getApplicationStates().Lob.lob

    this.agencyId = loginInfo.userInfo?.agencyInfo?.selectedAgencies[lob]?.agencyId
    if (environment.initData.brand.ASPID === 'MN') {
      this.isMN = true;
      if(!this.agencyId){
        console.error("No Agency ID Found");
      }
    }

    //if it is the passive referral for producer url, then only accept referral type of producer-url, if it is direct referral view, only accept email referrals, and quote emails, everything else can have any type of referral accepted. 
    this.referralTypes = this.isPassiveReferralView ? [REFERRALTYPE.PRODUCER_URL]: (this.isDirectReferralView ? [REFERRALTYPE.EMAILREFERRAL,REFERRALTYPE.LINKSHARING]: [REFERRALTYPE.EMAILREFERRAL, REFERRALTYPE.ENROLLMENTKIT, REFERRALTYPE.ROADMAPKIT, REFERRALTYPE.PLANOPTIONS, REFERRALTYPE.LINKSHARING]);

    if (this.showStatusFilter === "true") {
      this.showStatusFilter = true;
    } else {
      this.showStatusFilter = false;
    }

    this.referralStatusSteps = this.isPassiveReferralView ? [
        { value: 'clicked', label: 'Application Started' },
        { value: 'submittedApps', label: 'Application Submitted' }
      ] : (this.showAppInProgressStep ?
      [
        { value: 'emailSent', label: 'Email Sent' },
        { value: 'clicked', label: 'Link Clicked' },
        { value: 'appInProgess', label: 'Application in Progess'},
        { value: 'submittedApps', label: 'Application Submitted' }
      ] : 
      [
        { value: 'emailSent', label: 'Email Sent' },
        { value: 'clicked', label: 'Link Clicked' },
        { value: 'submittedApps', label: 'Application Submitted' }
      ]);
  
    await this.loadData();

    if (window.screen.width < 600) {
      this.width = 500;
      if(this.showLegend){
        this.options['legend'] = { position: 'top' };
      }
    } else {
      this.width = 1000;
      if(this.showLegend) {
        this.options['legend'] = { position: 'bottom', alignment: 'center', orientation: 'vertical' };
      }
    }
  }

  //dynamically assign chart max to avoid the -1 min of a chart when all data is 0 
  getChartMax(data){
    let maxArray = [];
    for(const counts of data){
      const copyForMax = [...counts]
      copyForMax.shift();
      maxArray = maxArray.concat(copyForMax);
    }
    const maxValueForChart = Math.max(...maxArray);
    if(maxValueForChart === 0){
      this.options.hAxis['maxValue'] = 1;
    }else{
      this.options.hAxis['maxValue'] = maxValueForChart;
    }
  }

  async loadData() {
    //this should now return only the referral types passed in to it 
    const statusCountParams = environment.initData.brand.ASPID === 'MN' ? this.agencyId : undefined;

    //purlStatusCounts - counts returned by service
    //displayCounts - counts/referral types that we actually want to display in the chart 

    const purlStatusCounts: PurlCount[] = await this.producerService.getPurlStatusCount(statusCountParams);
  //display counts should only be what referral values are in the referralTypes arrays
    let displayCounts =  purlStatusCounts.filter((counts) => { return this.referralTypes.includes(counts.referralType)});    
    this.columnNames = ['Events'];
    const emailSent: any[] = ['Email Sent'];
    const linkClicked: any[] = this.isPassiveReferralView ? ['Started'] : ['Link Clicked'];
    const appInProgess: any[] = ['Application in Progess'];
    const submitted: any[] = ['Submitted'];
    const zeroCountReferralTypes = [...this.referralTypes]
    let isMN = false;


      for (const displayReferral of displayCounts) {
      //0 counts are not returned from the service, so we need to manually add them to the chart
      //this removes the one's that DO exist from the array of all referral types
        if(zeroCountReferralTypes.includes(displayReferral.referralType)){
          zeroCountReferralTypes.splice(zeroCountReferralTypes.indexOf(displayReferral.referralType), 1);
        }
        this.columnNames.push(REFERRAL_TYPE_DISPLAY[displayReferral.referralType]);
        if(!this.isPassiveReferralView){
          emailSent.push(displayReferral.emailCount);
        }
        linkClicked.push(displayReferral.clickCount);
        if(!this.isPassiveReferralView){
          appInProgess.push(displayReferral.inprogressStatus);
        }
        submitted.push(displayReferral.submittedStatus);
    }

    //whatever doesn't exist remains in the zero count array and is manually added
    if(zeroCountReferralTypes.length > 0){
      for (const type of zeroCountReferralTypes) {
        this.columnNames.push(REFERRAL_TYPE_DISPLAY[type]);
        emailSent.push(0);
        linkClicked.push(0);
        appInProgess.push(0);
        submitted.push(0);
      }
    }

    if(!this.isPassiveReferralView){
      this.data.push(emailSent);
    }
    this.data.push(linkClicked)
    if (!isMN && !this.isPassiveReferralView && this.showAppInProgressStep) {
      this.data.push(appInProgess);
    }
    this.data.push(submitted);

    //get chart max value as soon as we have assigned data to avoid modification 
    this.getChartMax(this.data)
    this.search(this.searchForm, true);
  }

  clearSearch() {
    //this.appNumber = '';
    this.searchForm.reset();
    this.search(null, true);
  }

  setPageCounts() {
    if (this.lastAppNo > this.totalApplications) {
      this.lastAppNo = this.totalApplications;
    }
    this.totalPages = Math.ceil(this.totalApplications / this.referralsPerPage);
  }

  async search(form: UntypedFormGroup, isInitialLoad: boolean) {
    if (isInitialLoad) {
      this.resetInitialNumbers();
    }
    try {
      let fromDate = null;
      if (this.searchForm.controls['fromDate'].value) {
        fromDate
          = this.dateService.getDateAsFormattedString(
            this.searchForm.controls['fromDate'].value,
            'MM/DD/YYYY'
          );
      }
      let toDate = null;
      if (this.searchForm.controls['toDate'].value) {
        toDate = this.dateService.getDateAsFormattedString(
          this.searchForm.controls['toDate'].value,
          'MM/DD/YYYY'
        );
      }
      const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
      const email = this.searchForm.controls['emailId'].value;
      const agencyId = this.agencyId
      this.searchParams = {
        emailId: email ? email : undefined,
        fromDate: fromDate ? fromDate : undefined,
        toDate: toDate ? toDate : undefined,
        pageNumber: this.currentPage - 1,
        countPerPage: this.referralsPerPage,
        referralType: this.referralTypes
      };

      if (environment.initData.brand.ASPID === 'MN'){
        this.searchParams.agencyId = agencyId ? agencyId : undefined;
      }

      const response: PurlSearchResponse = await this.producerService.getReferrals(this.searchParams);
      this.bucketReferralStatuses(response.referralRetrive);

      this.totalApplications = response.totalCount;
      this.setPageCounts();
      this.referrals = [];

      if (this.selectedStep) {
        response.referralRetrive = response.referralRetrive.filter((referral) => {
          switch (this.selectedStep) {
            case 'emailSent':
              return referral.emailSent !== undefined;
            case 'clicked':
              return referral.clicked && referral.clicked.length > 0;
            case 'submittedApps':
              return referral.submittedApps && referral.submittedApps.length > 0;
            default:
              return true; //No filtering if selectedStep is not recognized
          };
        })
      };
      Array.prototype.push.apply(this.referrals, response.referralRetrive);

      if (this.referrals.length > 0) {
        this.hidePaginator = false;
      } else {
        this.hidePaginator = true;
      }

      this.triggerToRemoveAttr();
      return response;
    } catch (e) {
      console.log(e);
    }
  }

  resetInitialNumbers() {
    this.referralsPerPage = 10;
    this.currentPage = 1;
    this.totalApplications = 0;
    this.totalPages = 0;
    this.firstAppNo = 1;
  }

  goToReferralPage() {
    this.windowLocationService.goToUrl(this.referralPageUrl);
  }

  refreshReferrals() {
    this.totalPages = Math.ceil(this.totalApplications / this.referralsPerPage);
    this.search(this.searchForm, false);
    this.resetAppNo();
  }

  resetAppNo() {
    this.firstAppNo = this.referralsPerPage * (this.currentPage - 1) + 1;
    this.lastAppNo = this.referralsPerPage * this.currentPage;
  }

  referralStepEventSelection(event) {
    this.selectedStep = event.value;
    this.search(this.searchForm, false);
  }

  toggleMoreSearchOptions(showFilters) {
    showFilters = !showFilters;
  }

  bucketReferralStatuses(referrals) {
    const statusBucket: StatusBucket = {};

    referrals.forEach(referral => {
      if (referral.emailSent) {
        if (referral.clicked && referral.clicked.length > 0) {
          if (referral.submittedApps && referral.submittedApps.length > 0) {
            statusBucket.submittedApps = (statusBucket.submittedApps || 0) + 1;
          } else {
            statusBucket.clicked = (statusBucket.clicked || 0) + 1;
          }
        } else {
          statusBucket.emailSent = (statusBucket.emailSent || 0) + 1;
        }
      }
    });

    return statusBucket;
  }

  prev() {
    if (this.currentPage > 1) {
      --this.currentPage;
      this.resetAppNo();
      this.search(this.searchForm, false);
    }
  }

  next() {
    if (this.currentPage < this.totalPages) {
      ++this.currentPage;
      this.resetAppNo();
      this.search(this.searchForm, false);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (window.screen.width < 600) {
      this.width = 500;
      if(this.showLegend){
        this.options['legend'] = { position: 'top' };
      }
    } else {
      this.width = 1000;
      if(this.showLegend){
        this.options['legend'] = { position: 'bottom', alignment: 'center', orientation: 'vertical' };
      }
    }
  }

  private triggerToRemoveAttr() {
    google.load('visualization', 'current', { packages: ['corechart'] });
    const chart = new google.visualization.BarChart(document.getElementById('chartID'));
    const svg = chart.getContainer().getElementsByTagName('svg');
    if (svg.length > 0) {
      svg[0].removeAttribute('aria-label');
    }
  }
}
